import type { LottieRefCurrentProps } from "lottie-react";

import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  getCachedLottieData,
  getCachedLottieFrame,
  getLottieData,
  setCachedLottieFrame,
  type LottieVariant,
} from "./lottie-cache";

import { CONSTANTS } from "@/config/constants";
import { useAppTheme } from "@/hooks/use-app-theme";
import { useLogo } from "@/hooks/use-logo";

const Lottie = lazy(() => import("lottie-react"));

const FALLBACK_HEIGHT = 50;
const REPLYN_SLUG = "replyn";
const LOTTIE_SPEED = 1.25;

export const TenantLogoOrLottie = () => {
  const { full } = useLogo();
  const theme = useAppTheme();
  const isReplyn = CONSTANTS.APP_SLUG === REPLYN_SLUG;
  const variant: LottieVariant = theme === "light" ? "light" : "dark";

  const [animationData, setAnimationData] = useState<unknown | null>(() =>
    isReplyn ? getCachedLottieData(variant) : null,
  );

  const lottieRef = useRef<LottieRefCurrentProps>(null);

  useEffect(() => {
    if (!isReplyn) return;

    const cached = getCachedLottieData(variant);

    if (cached) {
      setAnimationData(cached);

      return;
    }

    let cancelled = false;

    setAnimationData(null);

    getLottieData(variant)
      .then((data) => {
        if (!cancelled) setAnimationData(data);
      })
      .catch(() => {
        // Silent fallback — keep showing <img>.
      });

    return () => {
      cancelled = true;
    };
  }, [isReplyn, variant]);

  useEffect(() => {
    if (!isReplyn) return;

    return () => {
      const frame = lottieRef.current?.animationItem?.currentFrame;

      if (typeof frame === "number") {
        setCachedLottieFrame(variant, frame);
      }
    };
  }, [isReplyn, variant]);

  const handleDOMLoaded = useCallback(() => {
    lottieRef.current?.setSpeed(LOTTIE_SPEED);
    const saved = getCachedLottieFrame(variant);

    if (saved > 0) {
      lottieRef.current?.goToAndPlay(saved, true);
    }
  }, [variant]);

  const fallbackImage = <img alt="" height={FALLBACK_HEIGHT} src={full} />;

  if (!isReplyn || !animationData) {
    return fallbackImage;
  }

  return (
    <Suspense fallback={fallbackImage}>
      <Lottie
        loop
        animationData={animationData}
        className=" w-[50vw]"
        lottieRef={lottieRef}
        onDOMLoaded={handleDOMLoaded}
      />
    </Suspense>
  );
};
